import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Droplets, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WateringScheduleManager = () => {
  const [rooms, setRooms] = useState([]);
  const [roomWaterings, setRoomWaterings] = useState({});
  const [todaySchedule, setTodaySchedule] = useState(null);
  const [statusRooms, setStatusRooms] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [roomsRes, scheduleRes, statusRes] = await Promise.all([
        axios.get(`${API}/rooms`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/watering/schedule`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/watering/today-status`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { rooms: [] } }))
      ]);
      
      setRooms(roomsRes.data);
      setTodaySchedule(scheduleRes.data);
      
      // Pre-fill with existing schedule data
      const waterings = {};
      scheduleRes.data.room_waterings?.forEach(rw => {
        waterings[rw.room_id] = {
          selected: true,
          water_liters: rw.water_liters,
          notes: rw.notes || ''
        };
      });

      // Also check status for watered rooms
      const statusList = statusRes.data.rooms || [];
      setStatusRooms(statusList);
      statusList.forEach(sr => {
        if (sr.completed && sr.actual_liters) {
          waterings[sr.room_id] = {
            selected: true,
            water_liters: sr.actual_liters,
            notes: waterings[sr.room_id]?.notes || '',
            watered: true
          };
        }
      });

      setRoomWaterings(waterings);
    } catch (error) {
      console.error('Failed to fetch watering data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleToggleRoom = (roomId, defaultWater) => {
    setRoomWaterings(prev => {
      if (prev[roomId]?.selected) {
        const newState = { ...prev };
        delete newState[roomId];
        return newState;
      } else {
        return {
          ...prev,
          [roomId]: {
            selected: true,
            water_liters: defaultWater || 0
          }
        };
      }
    });
  };

  const handleWaterAmountChange = (roomId, amount) => {
    setRoomWaterings(prev => ({
      ...prev,
      [roomId]: {
        ...prev[roomId],
        water_liters: parseFloat(amount) || 0
      }
    }));
  };

  const handleNotesChange = (roomId, notes) => {
    setRoomWaterings(prev => ({
      ...prev,
      [roomId]: {
        ...prev[roomId],
        notes
      }
    }));
  };

  const handleSubmit = async () => {
    const selectedRooms = Object.entries(roomWaterings)
      .filter(([_, data]) => data.selected)
      .map(([roomId, data]) => ({
        room_id: roomId,
        water_liters: data.water_liters,
        notes: (data.notes || '').trim() || null
      }));

    if (selectedRooms.length === 0) {
      toast.error('Please select at least one room');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/watering/schedule`, {
        room_waterings: selectedRooms
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Watering schedule updated: ${selectedRooms.length} rooms marked for today`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update schedule');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading...</div>;
  }

  const scheduledStatus = statusRooms.filter((r) => r.is_scheduled);
  const completedRooms = scheduledStatus.filter((r) => r.completed);
  const pendingRooms = scheduledStatus.filter((r) => !r.completed);

  return (
    <div className="space-y-6">
      {scheduledStatus.length > 0 && (
        <Card className="border-green-200" data-testid="watering-status-panel">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Today's Watering Status
              <Badge className="ml-1 bg-green-100 text-green-800 border-green-300" variant="outline" data-testid="watering-done-count">
                {completedRooms.length}/{scheduledStatus.length} done
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {scheduledStatus.map((r) => (
                <div
                  key={r.room_id}
                  className={`p-3 rounded-lg border-2 ${r.completed ? 'border-green-300 bg-green-50' : 'border-amber-200 bg-amber-50'}`}
                  data-testid={`watering-status-${r.room_number}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-gray-900">{r.room_name}</span>
                    {r.completed ? (
                      <Badge className="bg-green-600 text-white" data-testid={`watering-done-badge-${r.room_number}`}>
                        <CheckCircle className="w-3 h-3 mr-1" /> Done
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-500 text-white" data-testid={`watering-pending-badge-${r.room_number}`}>
                        <Clock className="w-3 h-3 mr-1" /> Pending
                      </Badge>
                    )}
                  </div>
                  {r.completed ? (
                    <div className="text-sm text-green-800 space-y-0.5">
                      <p className="flex items-center gap-1">
                        <Droplets className="w-3.5 h-3.5" />
                        <span className="font-medium">{r.actual_liters}L given</span>
                        {r.scheduled_liters != null && (
                          <span className="text-xs text-gray-500">(scheduled {r.scheduled_liters}L)</span>
                        )}
                      </p>
                      {r.completed_at && (
                        <p className="text-xs text-green-700 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {format(new Date(r.completed_at), 'h:mm a')}
                          {r.completed_by_name ? ` · by ${r.completed_by_name}` : ''}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-amber-800 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" />
                      Awaiting Water Tech{r.scheduled_liters != null ? ` · ${r.scheduled_liters}L` : ''}
                    </p>
                  )}
                </div>
              ))}
            </div>
            {pendingRooms.length === 0 && completedRooms.length > 0 && (
              <p className="text-sm text-green-700 mt-3 font-medium" data-testid="watering-all-done">
                ✓ All scheduled rooms have been watered today.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="border-blue-200" data-testid="watering-schedule-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Droplets className="w-5 h-5 text-blue-600" />
            Today's Watering Schedule
          </CardTitle>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="update-watering-schedule-button"
          >
            {submitting ? 'Updating...' : 'Update Schedule'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-600 mb-4">
          Select rooms that need watering today and enter water amount in liters for each room.
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rooms.map((room) => {
            const roomData = roomWaterings[room.id];
            const isSelected = roomData?.selected;
            const isWatered = roomData?.watered || todaySchedule?.rooms?.find(r => r.id === room.id)?.watered_today;
            const waterAmount = roomData?.water_liters ?? room.water_amount_liters ?? 0;
            
            return (
              <div
                key={room.id}
                className={`p-4 rounded-lg border-2 transition-all ${
                  isWatered ? 'border-green-300 bg-green-50' :
                  isSelected ? 'border-blue-400 bg-blue-50' :
                  'border-gray-200 bg-white'
                }`}
                data-testid={`room-watering-${room.room_number}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium">{room.name}</span>
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => handleToggleRoom(room.id, waterAmount || 0)}
                    data-testid={`checkbox-${room.room_number}`}
                  />
                </div>
                
                {isSelected && (
                  <div className="space-y-2">
                    <label className="text-xs text-gray-600 block">Water Amount (Liters)</label>
                    <Input
                      type="number"
                      step="0.1"
                      value={waterAmount}
                      onChange={(e) => handleWaterAmountChange(room.id, e.target.value)}
                      className="h-9"
                      data-testid={`water-amount-${room.room_number}`}
                    />
                    <label className="text-xs text-gray-600 block mt-2">
                      Notes for Water Tech <span className="text-gray-400">(optional, e.g. medicine to add)</span>
                    </label>
                    <Input
                      type="text"
                      placeholder="e.g. Add 5g Carbendazim, foliar only…"
                      value={roomData?.notes || ''}
                      onChange={(e) => handleNotesChange(room.id, e.target.value)}
                      className="h-9"
                      data-testid={`water-notes-${room.room_number}`}
                    />
                    {isWatered && (
                      <div className="text-xs text-green-600 mt-1">✓ Already watered - update amount if needed</div>
                    )}
                  </div>
                )}
                
                {!isSelected && (
                  <div className="text-sm text-gray-500">
                    Click checkbox to add
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {Object.keys(roomWaterings).filter(id => roomWaterings[id]?.selected).length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-900">
              <strong>{Object.keys(roomWaterings).filter(id => roomWaterings[id]?.selected).length}</strong> rooms selected for watering today
            </p>
          </div>
        )}
      </CardContent>
    </Card>
    </div>
  );
};

export default WateringScheduleManager;
